import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";

const empty = { from_path: "", to_path: "", status_code: 301, is_active: true, note: "" };

export default function Redirects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/redirects"); setItems(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/redirects/${editing.id}`, form);
      else await api.post("/admin/redirects", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete redirect?")) return;
    try { await api.delete(`/admin/redirects/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkImport = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const items = lines.map((l) => {
      const [from_path, to_path, sc] = l.split(/[,\t]/).map((s) => (s || "").trim());
      return { from_path, to_path, status_code: parseInt(sc || 301) || 301, is_active: true };
    }).filter((r) => r.from_path && r.to_path);
    if (!items.length) { toast.error("No valid rows found. Format: /old, /new, 301"); return; }
    try {
      const { data } = await api.post("/admin/redirects/bulk-import", items);
      toast.success(`Imported ${data.created}. Skipped ${data.skipped_duplicates} duplicates.`);
      setBulkOpen(false); setBulkText(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Redirects"
        subtitle="Map old URLs from the previous site to the new ones to preserve SEO."
        actions={
          <div className="flex gap-2">
            <Button onClick={() => setBulkOpen(true)} variant="outline" data-testid="bulk-import-btn">
              <UploadSimple size={16} className="mr-2" />Bulk import
            </Button>
            <Button onClick={openNew} data-testid="add-redirect-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
              <Plus size={16} className="mr-2" />New redirect
            </Button>
          </div>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="redirects-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
              <tr>
                <th className="px-6 py-3">From</th>
                <th className="px-6 py-3">To</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Active</th>
                <th className="px-6 py-3">Note</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-[#4B5563]">No redirects yet.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} data-testid={`redirect-row-${it.id}`}>
                  <td className="px-6 py-3 font-mono text-xs">{it.from_path}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#C2410C]">{it.to_path}</td>
                  <td className="px-6 py-3">
                    <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#C2410C]/10 text-[#C2410C]">
                      {it.status_code}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                      {it.is_active ? "On" : "Off"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-[#4B5563] text-xs max-w-xs truncate">{it.note || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="redirect-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit redirect" : "New redirect"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>From path *</Label><Input value={form.from_path} onChange={(e) => setForm({ ...form, from_path: e.target.value })} placeholder="/old-product-page" data-testid="red-from" /></div>
            <div className="space-y-2"><Label>To path *</Label><Input value={form.to_path} onChange={(e) => setForm({ ...form, to_path: e.target.value })} placeholder="/products/new-slug" data-testid="red-to" /></div>
            <div className="space-y-2">
              <Label>HTTP status</Label>
              <Select value={String(form.status_code)} onValueChange={(v) => setForm({ ...form, status_code: parseInt(v) })}>
                <SelectTrigger data-testid="red-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="301">301 Moved Permanently (recommended for SEO)</SelectItem>
                  <SelectItem value="302">302 Found (temporary)</SelectItem>
                  <SelectItem value="307">307 Temporary</SelectItem>
                  <SelectItem value="308">308 Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Note</Label><Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="red-note" /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="red-active" />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-redirect" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-dialog">
          <DialogHeader><DialogTitle>Bulk import redirects</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>One redirect per line — format: <code>from_path, to_path, status_code</code></Label>
            <Textarea rows={10} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"/old-1, /products/new-1, 301\n/old-2, /blogs/new-2, 301"} data-testid="bulk-text" className="font-mono text-xs" />
            <p className="text-xs text-[#4B5563]">Status code defaults to 301. Duplicates are skipped.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkImport} data-testid="run-bulk-import" className="bg-[#C2410C] hover:bg-[#9A3412]">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
